import { useState, useCallback, useEffect, useMemo } from 'react';
import { normalizeTimeZone, TimeFormat } from '@chronosphereio/core';
import constate from 'constate';
import {
  useGetUserTimeFormat,
  useGetUserTimeZone,
  useSetUserTimeFormat,
  useSetUserTimeZone,
} from '@/model/user-settings';

/**
 * Stores user's time zone and time format (12h or 24h) preferences
 */
function useUserTimePreferencesStore({ defaultTimeZone }: { defaultTimeZone?: string }) {
  const [timeZone, setTimeZoneState] = useState<string>(normalizeTimeZone(defaultTimeZone));
  const [timeFormat, setTimeFormatState] = useState<TimeFormat>('24h'); // default to 24h

  const { timeZoneSetting, loading: loadingTimeZone } = useGetUserTimeZone();
  const setUserTimeZone = useSetUserTimeZone();

  const { timeFormatSetting, loading: loadingTimeFormat } = useGetUserTimeFormat();
  const setUserTimeFormat = useSetUserTimeFormat();

  useEffect(() => {
    if (defaultTimeZone) {
      // provide defaultTimeZone when testing the app, in which case we will not load timeZone from grafana
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTimeZoneState(defaultTimeZone);
    } else if (timeZoneSetting && !loadingTimeZone) {
      // Initialize time zone from user settings.
      setTimeZoneState(timeZoneSetting);
    }
  }, [defaultTimeZone, loadingTimeZone, timeZoneSetting]);

  useEffect(() => {
    if (timeFormatSetting && !loadingTimeFormat) {
      // Initialize time format from user settings.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTimeFormatState(timeFormatSetting);
    }
  }, [loadingTimeFormat, timeFormatSetting]);

  const setTimeZone = useCallback(
    async (newTimeZone: string) => {
      const prevTimeZone = timeZone;
      try {
        // Save local state.
        setTimeZoneState(newTimeZone);
        // Persist state to user settings.
        await setUserTimeZone(newTimeZone);
      } catch (e) {
        setTimeZoneState(prevTimeZone);
        await setUserTimeZone(prevTimeZone || '');
        throw e;
      }
    },
    [setUserTimeZone, timeZone]
  );

  const setTimeFormat = useCallback(
    async (newTimeFormat: TimeFormat) => {
      const prevTimeFormat = timeFormat;
      try {
        // Save local state.
        setTimeFormatState(newTimeFormat);
        // Persist state to user settings.
        await setUserTimeFormat(newTimeFormat);
      } catch (e) {
        setTimeFormatState(prevTimeFormat);
        await setUserTimeFormat(prevTimeFormat || '');
        throw e;
      }
    },
    [setUserTimeFormat, timeFormat]
  );

  return {
    timeZone,
    setTimeZone,
    timeFormat,
    setTimeFormat,
  };
}

export const [UserTimePreferencesProvider, useUserTimeZone, useUserTimeFormat] = constate(
  useUserTimePreferencesStore,
  (value) =>
    useMemo(() => ({ timeZone: value.timeZone, setTimeZone: value.setTimeZone }), [value.timeZone, value.setTimeZone]), // becomes useUserTimeZone
  (value) =>
    useMemo(
      () => ({ timeFormat: value.timeFormat, setTimeFormat: value.setTimeFormat }),
      [value.timeFormat, value.setTimeFormat]
    ) // becomes useUserTimeFormat
);
